"use client";

import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

import { getOrCreateHomeEntryAttribution } from "@/utils/attribution";
import { getOrCreateHomeSessionId } from "@/utils/homeSession";
import { AbExperiment, getOrAssignExperimentVariant } from "@/utils/abTesting";
import { trackEvent } from "@/utils/analytics";

type UseAbExperimentTrackingMode = "exposure" | "page_view" | "none";

type UseAbExperimentOptions = {
  trackingMode?: UseAbExperimentTrackingMode;
};

function getTrackingSessionKey(experimentId: string, variant: string, path: string, trackingMode: UseAbExperimentTrackingMode) {
  return `sr_ab_tracking:${trackingMode}:${experimentId}:${variant}:${path}`;
}

function detectBrowserContext() {
  if (typeof navigator === "undefined") return { user_agent_class: "unknown", is_meta_in_app: false };
  const ua = navigator.userAgent || "";
  const lower = ua.toLowerCase();
  const isMetaInApp =
    lower.includes("fban") ||
    lower.includes("fbav") ||
    lower.includes("instagram") ||
    lower.includes("meta");

  return {
    user_agent_class: isMetaInApp ? "meta_in_app_browser" : "standard_browser",
    is_meta_in_app: isMetaInApp,
  };
}

export function useAbExperiment(experiment: AbExperiment, { trackingMode = "exposure" }: UseAbExperimentOptions = {}) {
  const path = usePathname();
  const [variant, setVariant] = useState(experiment.variants[0]?.key ?? "control");
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const assignedVariant = getOrAssignExperimentVariant(experiment);
    setVariant(assignedVariant);
    setIsReady(true);

    if (trackingMode === "none") return;

    const trackingSessionKey = getTrackingSessionKey(experiment.id, assignedVariant, path, trackingMode);
    const browserContext = detectBrowserContext();
    const homeSessionId = getOrCreateHomeSessionId();
    const attributionContext = getOrCreateHomeEntryAttribution();

    if (sessionStorage.getItem(trackingSessionKey)) {
      const skipLogKey = `${trackingSessionKey}:skip_logged`;
      if (!sessionStorage.getItem(skipLogKey)) {
        sessionStorage.setItem(skipLogKey, "1");
        trackEvent("ab_skipped", path, {
          reason: "already_tracked",
          experiment_id: experiment.id,
          variant: assignedVariant,
          home_session_id: homeSessionId,
          page_location: window.location.pathname,
          ...browserContext,
          ...attributionContext,
        });
      }
      return;
    }

    sessionStorage.setItem(trackingSessionKey, "1");

    trackEvent("ab_exposed", path, {
      experiment_id: experiment.id,
      variant: assignedVariant,
      home_session_id: homeSessionId,
      page_location: window.location.pathname,
      ...browserContext,
      ...attributionContext,
    });
    trackEvent(`ab_exposed_${assignedVariant}`, path, {
      experiment_id: experiment.id,
      variant: assignedVariant,
      home_session_id: homeSessionId,
      page_location: window.location.pathname,
      ...browserContext,
      ...attributionContext,
    });

    if (trackingMode === "page_view") {
      trackEvent("page_view", path, {
        experiment_id: experiment.id,
        variant: assignedVariant,
        home_session_id: homeSessionId,
        page_location: window.location.pathname,
        ...browserContext,
        ...attributionContext,
      });
      return;
    }
  }, [experiment, path, trackingMode]);

  return { variant, isReady };
}
